import React from "react";
import { useNavigate } from "react-router-dom";

interface ServiceFeature {
  title: string;
  description: string;
  icon: JSX.Element;
}

const MaintenanceServices: React.FC = () => {
  const navigate = useNavigate();

  const features: ServiceFeature[] = [
    {
      title: "Preventive Maintenance",
      description:
        "Regular inspection and maintenance to prevent equipment failures and extend machinery lifespan.",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
          />
        </svg>
      ),
    },
    {
      title: "Emergency Repairs",
      description:
        "24/7 emergency repair services to minimize downtime and restore operations quickly.",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 10V3L4 14h7v7l9-11h-7z"
          />
        </svg>
      ),
    },
    {
      title: "Equipment Upgrades",
      description:
        "Modernization and upgrades of existing equipment to improve performance and efficiency.",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
      ),
    },
  ];

  const services = [
    {
      title: "Preventive Services",
      items: [
        "Regular Inspections",
        "Equipment Calibration",
        "Parts Replacement",
        "Performance Testing",
      ],
    },
    {
      title: "Repair Services",
      items: [
        "Emergency Repairs",
        "Troubleshooting",
        "Component Replacement",
        "System Restoration",
      ],
    },
    {
      title: "Maintenance Programs",
      items: [
        "Scheduled Maintenance",
        "Condition Monitoring",
        "Maintenance Planning",
        "Documentation",
      ],
    },
  ];

  const benefits = [
    "Reduced Equipment Downtime",
    "Extended Equipment Life",
    "Improved Safety Standards",
    "Cost-Effective Operations",
    "Enhanced Performance",
    "Compliance with Standards",
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white py-24">
        <div className="absolute inset-0">
          <img
            src="/about.jpg"
            alt="Maintenance Services"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black opacity-60"></div>
        </div>
        <div className="relative container mx-auto px-4">
          <h1 className="text-5xl font-bold mb-6">
            Equipment Maintenance & Repair
          </h1>
          <p className="text-lg text-gray-300 max-w-3xl">
            Comprehensive maintenance and repair services to keep your equipment
            running at peak performance. Available 24/7 for emergency repairs.
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="text-orange-500 mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Process */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">
            Our Maintenance Services
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-lg">
                <h3 className="text-xl font-bold mb-4 text-orange-500">
                  {service.title}
                </h3>
                <ul className="space-y-3">
                  {service.items.map((item, idx) => (
                    <li key={idx} className="flex items-center text-gray-600">
                      <svg
                        className="w-5 h-5 text-orange-500 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-3xl font-bold mb-6">
              Benefits of Regular Maintenance
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <p className="text-gray-600 mb-4">
                  Regular maintenance provides numerous benefits for your
                  equipment and operations:
                </p>
                <ul className="space-y-3">
                  {benefits.map((item, index) => (
                    <li key={index} className="flex items-center text-gray-600">
                      <svg
                        className="w-5 h-5 text-orange-500 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <img
                  src="/quality.jpg"
                  alt="Maintenance Benefits"
                  className="rounded-lg shadow-md"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Contact */}
      <section className="py-16 bg-orange-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            24/7 Emergency Maintenance Service
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">
            Equipment breakdown? Contact our emergency maintenance team
            immediately. We're available 24/7 to minimize your downtime.
          </p>
          <div className="flex justify-center space-x-4">
            <button
              onClick={() => navigate("/contacts")}
              className="bg-white text-orange-500 px-8 py-3 rounded hover:bg-gray-100 transition-colors"
            >
              Contact Emergency Team
            </button>
            <button
              onClick={() => navigate("/projects")}
              className="border-2 border-white text-white px-8 py-3 rounded hover:bg-white hover:text-orange-500 transition-colors"
            >
              View Our Work
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MaintenanceServices;
